import { normalize } from '@/data/Content/RepurchaseExclusionRules';
import { RECOM_PARTNUMBERS_MAX } from '@/data/recom/constants';
import { ProductType } from '@/data/types/Product';
import { findProductAttribute } from 'integration/utils/findProductAttribute';

const FAMILY_ATTRIBUTE = 'Familia';
const GENDER_ATTRIBUTE = 'Genero';

export type FamilyPoolProduct = Pick<ProductType, 'partNumber' | 'attributes'>;

export type FamilyPoolSpots = {
	hombre?: string;
	mujer?: string;
	nino?: string;
};

const SPOT_BY_GENDER: Record<string, keyof FamilyPoolSpots> = {
	hombre: 'hombre',
	mujer: 'mujer',
	nino: 'nino',
	nina: 'nino',
};

const spotOfGender = (gender?: string) => SPOT_BY_GENDER[normalize(gender)];

export const excludeSeedGender = (spots: FamilyPoolSpots, gender?: string): FamilyPoolSpots => {
	const excluded = spotOfGender(gender);
	return excluded ? { ...spots, [excluded]: undefined } : spots;
};

export const withoutSeedGender = <T extends FamilyPoolProduct>(products: T[], gender?: string) => {
	const excluded = spotOfGender(gender);
	return excluded
		? products.filter(
				({ attributes }) =>
					spotOfGender(findProductAttribute(attributes, GENDER_ATTRIBUTE)?.value) !== excluded
		  )
		: products;
};

export const parseFamilyPriority = (value = '') => value.split(',').map(normalize).filter(Boolean);

export const limitFamilyPoolSize = (size: number, genders: number) =>
	Math.min(size, Math.floor(RECOM_PARTNUMBERS_MAX / Math.max(1, genders)));

const familyOf = ({ attributes }: FamilyPoolProduct) =>
	normalize(findProductAttribute(attributes, FAMILY_ATTRIBUTE)?.value);

export const pickVarietyPool = (
	products: FamilyPoolProduct[],
	{ size, priority = [] }: { size: number; priority?: string[] }
): string[] => {
	const groups = new Map<string, string[]>();
	products.forEach((product) => {
		const family = familyOf(product);
		groups.set(family, [...(groups.get(family) ?? []), product.partNumber]);
	});

	const rank = (family: string) => {
		const index = priority.indexOf(family);
		return index === -1 ? priority.length : index;
	};
	const queues = [...groups.entries()]
		.sort(([a], [b]) => rank(a) - rank(b))
		.map(([, partNumbers]) => Array.from(new Set(partNumbers)));
	const rounds = Math.max(0, ...queues.map((queue) => queue.length));

	const interleaved = Array.from({ length: rounds }, (_, round) =>
		queues.flatMap((queue) => (round < queue.length ? [queue[round]] : []))
	).flat();

	return Array.from(new Set(interleaved)).slice(0, Math.max(0, size));
};
